import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";

import { ExportCta } from "components";
import FGTrackingTable from "../components/FGTrackingTable";
import FGRefreshContext from "../FGRefreshContext";
import { SellerList } from "./components";

import { getCall, postCall_v2 } from "services";
import useDownload from "hooks/useDownload";
import { formatDialogDate } from "utils";
import { normalizeFgTrackingData } from "../makeData";
import {
  buildOrdersSearchPayload,
  buildSellerAnalyticsPayload,
  buildSellerFilters,
  getNextSellerSort,
  getSellerVmIds,
  normalizeSellerAnalyticsData,
} from "./makeData";
import {
  DASHBOARD_TABLE_FILTERS,
  DEFAULT_SELLER_SORT,
  EMPTY_FG_TRACKING_DATA,
  SORT_FIELD_MAP,
} from "./constants";

const SELLER_ANALYTICS_ENDPOINT =
  "/joms/api/internal/v1/finished-goods/fetch-sellers";

const ORDERS_SEARCH_ENDPOINT = "/joms/api/v2/fg-tracking/orders/search";

const Dashboard = () => {
  const { downloadDocumentByPost } = useDownload();
  const [sellerData, setSellerData] = useState([]);
  const [sellerPagination, setSellerPagination] = useState({});
  const [sellerList, setSellerList] = useState([]);

  const [selectedVmIds, setSelectedVmIds] = useState([]);
  const [filters, setFilters] = useState({});
  const [sellerSort, setSellerSort] = useState(DEFAULT_SELLER_SORT);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [customersData, setCustomersData] = useState([]);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [fgData, setFgData] = useState(EMPTY_FG_TRACKING_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [isSellerLoading, setIsSellerLoading] = useState(true);
  const [isSellerInitialized, setIsSellerInitialized] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [selectedLineItemsByOrder, setSelectedLineItemsByOrder] = useState({});
  const [sortConfig, setSortConfig] = useState({
    sortBy: "aging",
    sortDir: "ASC",
  });
  const [tableFilters, setTableFilters] = useState(["fgduepend"]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const setSellerAnalyticsData = useCallback((response) => {
    const normalized = normalizeSellerAnalyticsData(response);
    setSellerData(normalized.sellers);
    setSellerPagination(normalized.pagination);
    setSelectedVmIds(getSellerVmIds(normalized.sellers));
  }, []);

  const fetchAllSellers = useCallback(async () => {
    try {
      const res = await getCall(
        "/joms/api/internal/v1/finished-goods/fetch-fg-lineitem-sellers",
      );
      const sellers = Array.isArray(res?.data) ? res.data : [];
      setSellerList(sellers);
    } catch {
      setSellerList([]);
    }
  }, []);

  const fetchSellerAnalytics = useCallback(
    async (
      activeFilters = {},
      activeSort = DEFAULT_SELLER_SORT,
      sellerPage = 0,
      sellerRowsPerPage = 10,
    ) => {
      let wasCancelled = false;
      setIsSellerLoading(true);
      try {
        const res = await postCall_v2(
          SELLER_ANALYTICS_ENDPOINT,
          buildSellerAnalyticsPayload(
            activeFilters,
            activeSort,
            sellerPage,
            sellerRowsPerPage,
          ),
        );

        if (!res) {
          wasCancelled = true;
          return;
        }

        if (res?.data?.success) {
          setSellerAnalyticsData(res?.data);
        } else {
          setSellerAnalyticsData({ data: {} });
        }
      } catch {
        setSellerAnalyticsData({ data: {} });
      } finally {
        if (!wasCancelled) {
          setIsSellerLoading(false);
          setIsSellerInitialized(true);
        }
      }
    },
    [setSellerAnalyticsData],
  );

  const fetchCustomerData = useCallback(async (customerInput) => {
    if (!customerInput) {
      setCustomersData([]);
      return;
    }

    try {
      setBuyerLoading(true);
      setCustomersData([]);
      const res = await getCall(
        `/joms/api/external/v1/customer-details-by-reference/${customerInput}`,
      );
      setCustomersData(
        (res?.data ?? []).map(({ companyName, gstin }) => ({
          companyName,
          gstin: gstin || "",
        })),
      );
    } catch {
      setCustomersData([]);
    } finally {
      setBuyerLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSellerAnalytics({}, DEFAULT_SELLER_SORT);
  }, [fetchSellerAnalytics]);

  useEffect(() => {
    fetchAllSellers();
  }, [fetchAllSellers]);

  const handleApplyFilters = useCallback(
    (filterPayload = {}) => {
      if (!Object.keys(filterPayload).length) {
        setFilters({});
        setSelectedVmIds([]);
        setFgData(EMPTY_FG_TRACKING_DATA);
        setExpandedRows([]);
        setSelectedLineItemsByOrder({});
        setPage(0);
        fetchSellerAnalytics({}, sellerSort);
        return;
      }

      const nextFilters = buildSellerFilters(filterPayload, sellerList);

      setFilters(nextFilters);
      setSelectedVmIds([]);
      setExpandedRows([]);
      setSelectedLineItemsByOrder({});
      setPage(0);
      fetchSellerAnalytics(nextFilters, sellerSort);
    },
    [fetchSellerAnalytics, sellerList, sellerSort],
  );

  const handleToggleSeller = useCallback((vmId) => {
    setPage(0);
    setExpandedRows([]);
    setSelectedLineItemsByOrder({});
    setSelectedVmIds((prev) =>
      prev.includes(vmId) ? prev.filter((id) => id !== vmId) : [...prev, vmId],
    );
  }, []);

  const handleToggleAllSellers = useCallback((vmIds) => {
    setPage(0);
    setExpandedRows([]);
    setSelectedLineItemsByOrder({});
    setSelectedVmIds(vmIds);
  }, []);

  const handleClearSelection = useCallback(() => {
    setFilters({});
    setSelectedVmIds([]);
    setSellerSort(DEFAULT_SELLER_SORT);
    setFilterResetKey((previousKey) => previousKey + 1);
    setCustomersData([]);
    setFgData(EMPTY_FG_TRACKING_DATA);
    setExpandedRows([]);
    setSelectedLineItemsByOrder({});
    setPage(0);
    fetchSellerAnalytics({}, DEFAULT_SELLER_SORT, 0, 10);
  }, [fetchSellerAnalytics]);

  const handleSellerSortChange = useCallback(
    (sortBy) => {
      const nextSort = getNextSellerSort(sellerSort, sortBy);

      setSellerSort(nextSort);
      fetchSellerAnalytics(filters, nextSort);
    },
    [fetchSellerAnalytics, filters, sellerSort],
  );

  const handleSellerPageChange = useCallback(
    (event, nextPage) => {
      fetchSellerAnalytics(
        filters,
        sellerSort,
        nextPage,
        Number(sellerPagination?.pageSize || 20),
      );
    },
    [fetchSellerAnalytics, filters, sellerPagination?.pageSize, sellerSort],
  );

  const handleSellerRowsPerPageChange = useCallback(
    (event) => {
      fetchSellerAnalytics(filters, sellerSort, 0, Number(event.target.value));
    },
    [fetchSellerAnalytics, filters, sellerSort],
  );

  const fetchOrders = useCallback(async () => {
    if (!isSellerInitialized) {
      return;
    }

    let wasCancelled = false;
    setIsLoading(true);
    try {
      const payload = buildOrdersSearchPayload({
        page,
        rowsPerPage,
        filters,
        selectedVmIds,
        tableFilters,
        sortConfig,
      });

      const res = await postCall_v2(
        ORDERS_SEARCH_ENDPOINT,
        payload,
        {},
        {},
        "dashboard-orders-search",
      );

      if (!res) {
        wasCancelled = true;
        return;
      }

      if (res?.data) {
        setFgData(normalizeFgTrackingData(res?.data));
      } else {
        setFgData(EMPTY_FG_TRACKING_DATA);
      }
    } catch {
      setFgData(EMPTY_FG_TRACKING_DATA);
    } finally {
      if (!wasCancelled) {
        setIsLoading(false);
      }
    }
  }, [
    selectedVmIds,
    filters,
    page,
    rowsPerPage,
    sortConfig,
    tableFilters,
    isSellerInitialized,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const pagination = useMemo(
    () => ({
      page,
      rowsPerPage,
      totalCount:
        fgData.pagination?.totalRecords || fgData.pagination?.totalItems || 0,
    }),
    [page, rowsPerPage, fgData],
  );

  const handlePageChange = useCallback((_event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleToggleExpand = useCallback((orderId) => {
    setExpandedRows((previousRows) =>
      previousRows.includes(orderId)
        ? previousRows.filter((id) => id !== orderId)
        : [...previousRows, orderId],
    );
  }, []);

  const handleToggleLineItem = useCallback((orderId, lineItemId) => {
    setSelectedLineItemsByOrder((previousMap) => {
      const currentSelected = previousMap[orderId] || [];
      const isSelected = currentSelected.includes(lineItemId);

      return {
        ...previousMap,
        [orderId]: isSelected
          ? currentSelected.filter((id) => id !== lineItemId)
          : [...currentSelected, lineItemId],
      };
    });
  }, []);

  const handleToggleSelectAll = useCallback(
    (orderId, allEligibleSelected) => {
      const order = (fgData.orders || []).find(
        (item) => item.orderId === orderId,
      );
      const eligibleIds = (order?.lineItems || [])
        .filter((item) => item.eligible)
        .map((item) => item.id);

      setSelectedLineItemsByOrder((previousMap) => ({
        ...previousMap,
        [orderId]: allEligibleSelected ? [] : eligibleIds,
      }));
    },
    [fgData.orders],
  );

  const handleFgSortChange = useCallback((field) => {
    const apiField = SORT_FIELD_MAP[field];

    if (!apiField) {
      return;
    }

    setSortConfig((prev) => ({
      sortBy: apiField,
      sortDir:
        prev.sortBy === apiField && prev.sortDir === "ASC" ? "DESC" : "ASC",
    }));
    setPage(0);
  }, []);

  const handleToggleTableFilter = useCallback((key) => {
    setTableFilters((previousFilters) =>
      previousFilters.includes(key)
        ? previousFilters.filter((filterKey) => filterKey !== key)
        : [...previousFilters, key],
    );
    setExpandedRows([]);
    setSelectedLineItemsByOrder({});
    setPage(0);
  }, []);

  const exportPayload = useCallback(() => {
    return {
      fgTab: tableFilters,
      buyerOrderNo: filters.buyerOrderNo || "",
      sellerOrderNo: filters.sellerOrderNo || "",
      customerNames: filters.customerNames || [],
      deliveryType: filters.deliveryType || "",
      orderType: filters.orderType || "",
      dateType: filters.dateType || "ORD_CNF",
      dateFrom: filters.dateFrom || "",
      dateTo: filters.dateTo || "",
      vmIds: selectedVmIds,
      sortBy: sortConfig.sortBy,
      sortDir: sortConfig.sortDir,
    };
  }, [tableFilters, page, rowsPerPage, filters, selectedVmIds, sortConfig]);

  const handleExport = useCallback(async () => {
    const payload = exportPayload();
    await downloadDocumentByPost(
      `/joms/api/internal/v1/finished-goods/export`,
      payload,
      `fg-tracking-export-${formatDialogDate(Date.now())}.xlsx`,
    );
  }, [exportPayload]);

  const getTableFilterCount = useCallback(
    (countKey) => Number(fgData.tabCounts?.[countKey] || 0),
    [fgData.tabCounts],
  );

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 0, alignItems: "stretch" }}>
        <Box sx={{ width: 290 }}>
          <SellerList
            sellerData={sellerData}
            selectedVmIds={selectedVmIds}
            onToggleSeller={handleToggleSeller}
            onToggleAllSellers={handleToggleAllSellers}
            onClearSelection={handleClearSelection}
            onSortChange={handleSellerSortChange}
            onApplyFilters={handleApplyFilters}
            sellerFilterData={sellerList}
            customersData={customersData}
            fetchCustomersData={fetchCustomerData}
            buyerLoading={buyerLoading}
            sortConfig={sellerSort}
            sellerPagination={sellerPagination}
            onPageChange={handleSellerPageChange}
            onRowsPerPageChange={handleSellerRowsPerPageChange}
            filterResetKey={filterResetKey}
            loading={isSellerLoading}
          />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 4,
              border: "1px solid",
              borderLeft: 0,
              borderColor: "grey.300",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "common.white",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
              }}
            >
              {DASHBOARD_TABLE_FILTERS.map((filter, index) => {
                const selected = tableFilters.includes(filter.key);
                const Icon = filter.icon;

                return (
                  <Box
                    key={filter.key}
                    component="button"
                    type="button"
                    onClick={() => handleToggleTableFilter(filter.key)}
                    sx={{
                      minHeight: 38,
                      px: 1.75,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1.25,
                      border: 0,
                      borderLeft:
                        index === 0 ? 0 : "1px solid rgba(148, 163, 184, 0.35)",
                      backgroundColor: selected
                        ? "primary.main"
                        : "common.white",
                      cursor: "pointer",
                    }}
                  >
                    {filter.tooltip ? (
                      <Tooltip title={filter.tooltip} arrow>
                        <Icon
                          sx={{
                            fontSize: 16,
                            color: selected ? "common.white" : "grey.A1000",
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Icon
                        sx={{
                          fontSize: 16,
                          color: selected ? "common.white" : "grey.A1000",
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: selected ? "common.white" : "grey.A1000",
                      }}
                    >
                      {filter.label}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        minWidth: 20,
                        height: 18,
                        px: 0.75,
                        borderRadius: 9,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        color: selected ? "primary.main" : "text.secondary",
                        backgroundColor: selected ? "common.white" : "grey.200",
                      }}
                    >
                      {getTableFilterCount(filter.countKey)}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                height: 38,
                px: 1.75,
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                "& > .MuiBox-root": {
                  p: "0 !important",
                  gap: 0.75,
                  alignItems: "center",
                },
                "& .MuiTypography-root": {
                  fontSize: 13,
                  fontWeight: 600,
                },
              }}
            >
              <ExportCta onExport={handleExport} />
            </Box>
          </Box>

          <Box m={3}>
            <FGRefreshContext.Provider value={fetchOrders}>
              <FGTrackingTable
                isLoading={isLoading}
                orders={fgData.orders || []}
                pagination={pagination}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                expandedRows={expandedRows}
                onToggleExpand={handleToggleExpand}
                selectedLineItemsByOrder={selectedLineItemsByOrder}
                onToggleLineItem={handleToggleLineItem}
                onToggleSelectAll={handleToggleSelectAll}
                sortConfig={sortConfig}
                onSortByToggle={handleFgSortChange}
                onSortDirToggle={handleFgSortChange}
              />
            </FGRefreshContext.Provider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
